import React, { useEffect } from 'react';
import Layout from '../components/layout/Layout';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import CTASection from '../components/shared/CTASection';
import { ParallaxSection, ParallaxImage, ParallaxElement, FadeInOnScroll } from '../components/shared/Parallax';
import { heroContent, vetrateAdvantages, installatoriInterni } from '../data/mock';
import { Eye, Thermometer, Lock, Maximize, Sparkles, Volume2, Building2, Sun, Users, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const VetratePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: Eye,
      title: "Trasparenza Totale",
      description: "Vetri con profili minimali che massimizzano la vista e la luminosità naturale."
    },
    {
      icon: Thermometer,
      title: "Isolamento Termico",
      description: "Vetri camera a bassa emissività per un comfort ottimale in ogni stagione."
    },
    {
      icon: Maximize,
      title: "Apertura Totale",
      description: "Sistemi scorrevoli impacchettabili che scompaiono completamente."
    },
    {
      icon: Lock,
      title: "Sicurezza Certificata",
      description: "Vetri temperati e stratificati con serrature di sicurezza multipoint."
    },
    {
      icon: Volume2,
      title: "Abbattimento Acustico",
      description: "Protezione dai rumori esterni per un ambiente silenzioso e rilassante."
    },
    {
      icon: Sparkles,
      title: "Facilità di Pulizia",
      description: "Trattamenti autopulenti e accesso facilitato per la manutenzione."
    }
  ];

  return (
    <Layout>
      {/* Hero Section with Video */}
      <HeroSection
        title={heroContent.vetrate.title}
        subtitle={heroContent.vetrate.subtitle}
        ctaText="Richiedi una progettazione personalizzata"
        ctaLink="/preventivo"
        videoUrl="https://customer-assets.emergentagent.com/job_828fb547-63e8-4146-aaa2-2aed84a5e0d5/artifacts/io4jgfvk_video%20bioclimatica%20sardegna%20vetri.mp4"
        imageUrl={heroContent.vetrate.imageUrl}
        showScrollIndicator={true}
      />

      {/* Intro Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  Vetrate Panoramiche
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 leading-tight mb-6">
                  La continuità tra interno ed esterno
                </h2>
                <p className="text-lg text-gray-600 font-light leading-relaxed mb-6">
                  Le vetrate panoramiche trasformano radicalmente la percezione degli spazi, 
                  eliminando le barriere visive e creando ambienti luminosi e ariosi.
                </p>
                <p className="text-lg text-gray-600 font-light leading-relaxed">
                  Perfette per chiudere pergole, verande e terrazzi, le nostre vetrate combinano 
                  estetica minimale, prestazioni termiche elevate e massima praticità d'uso.
                </p>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <ParallaxImage
                src="https://customer-assets.emergentagent.com/job_828fb547-63e8-4146-aaa2-2aed84a5e0d5/artifacts/43ms0r1n_vetri%20sardegna.png"
                alt="Vetrata panoramica - continuità tra interno ed esterno"
                speed={0.15}
                aspectRatio="4/5"
              />
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Montatori Interni Banner */}
      <section className="py-10 bg-gray-900 text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
            <Users className="w-12 h-12 text-sky-400" />
            <div>
              <h3 className="text-xl font-medium mb-1">{installatoriInterni.title}</h3>
              <p className="text-gray-300 font-light">{installatoriInterni.text}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 lg:py-32 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Caratteristiche"
              title="Vetrate di qualità superiore"
              description="Ogni dettaglio è progettato per offrire prestazioni eccellenti e un'estetica impeccabile."
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <div 
                  className="p-8 bg-white border border-gray-100 hover:border-sky-200 transition-all duration-500 hover:shadow-xl group"
                >
                  <div className="w-14 h-14 rounded-full bg-sky-500/10 flex items-center justify-center mb-6 group-hover:bg-sky-500 transition-colors duration-300">
                    <feature.icon className="w-6 h-6 text-sky-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 font-light leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* NUOVA SEZIONE: Vetrate per Uffici */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-amber-500">
                  VEPA - Vetrate Panoramiche
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 leading-tight mb-6">
                  Trasforma la tua pergola in uno spazio vivibile tutto l'anno
                </h2>
                <p className="text-lg text-gray-600 font-light leading-relaxed mb-6">
                  Le <strong>VEPA (Vetrate Panoramiche Scorrevoli)</strong> sono la soluzione ideale per 
                  chiudere la tua <strong>pergotenda o bioclimatica</strong> e renderla utilizzabile 
                  anche nei mesi più freddi. Immagina di goderti il tuo spazio esterno anche a dicembre, 
                  protetto dal vento e dal freddo, ma con tutta la luce naturale.
                </p>
                <p className="text-lg text-gray-600 font-light leading-relaxed mb-6">
                  I pannelli in vetro temperato scorrono silenziosamente su binari in alluminio, 
                  permettendo un'<strong>apertura totale in estate</strong> e una <strong>chiusura ermetica in inverno</strong>. 
                  La tua pergola diventa una vera e propria stanza in più, aumentando il valore del tuo immobile.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span><strong>Vivibilità 365 giorni</strong> - Usa la tua pergola anche in inverno</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span><strong>Protezione totale</strong> - Vento, pioggia e freddo restano fuori</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span><strong>Apertura totale</strong> - In estate i pannelli si impacchettano completamente</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span><strong>Vetro temperato</strong> - Sicurezza e resistenza garantite</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span><strong>Aumento valore immobile</strong> - Un investimento che si ripaga</span>
                  </li>
                </ul>
                <Link to="/preventivo">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 rounded-none">
                    Chiudi la tua Pergola con VEPA
                  </Button>
                </Link>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <div className="bg-white aspect-[4/3] flex items-center justify-center rounded-lg overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/2c47kngv_biocliamtica%20vetrate.png"
                  alt="Bioclimatica con vetrate VEPA - Vivibile tutto l'anno"
                  className="w-full h-full object-cover"
                />
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* NUOVA SEZIONE: Vetrata con Tenda Integrata (Chiusura Balconi) */}
      <section className="py-24 lg:py-32 bg-gradient-to-br from-sky-50 to-blue-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll className="order-2 lg:order-1">
              <div className="bg-white p-4 rounded-lg shadow-lg">
                <div className="aspect-[4/3] rounded-lg overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/rxtga1k4_z-glass-1-2.webp"
                    alt="Vetrata con tenda da sole integrata - Sistema 2 in 1"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-sm text-gray-500 text-center mt-4">
                  Sistema brevettato con doppia funzione: vetrata + tenda da sole nella stessa guida
                </p>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200} className="order-1 lg:order-2">
              <div>
                <span className="inline-flex items-center gap-2 text-sm font-medium tracking-widest uppercase mb-4 text-amber-600">
                  <Sun className="w-4 h-4" />
                  Soluzione 2-in-1
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 leading-tight mb-6">
                  Vetrata con Tenda da Sole Integrata
                </h2>
                <p className="text-lg text-gray-600 font-light leading-relaxed mb-6">
                  Una soluzione innovativa per i <strong>balconi e le chiusure perimetrali</strong>: 
                  la guida della vetrata integra anche il sistema di tenda da sole. 
                  Due funzioni in un unico prodotto, per la massima versatilità.
                </p>
                <div className="bg-white p-6 rounded-lg border border-amber-200 mb-8">
                  <h4 className="font-medium text-gray-900 mb-3">Come funziona:</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-sm font-medium text-amber-700">1</span>
                      <span>La guida perimetrale ospita sia le vetrate che la tenda da sole</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-sm font-medium text-amber-700">2</span>
                      <span>D'inverno chiudi con le vetrate per proteggere dal freddo</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-sm font-medium text-amber-700">3</span>
                      <span>D'estate apri le vetrate e proteggi dal sole con la tenda</span>
                    </li>
                  </ul>
                </div>
                <Link to="/preventivo">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 rounded-none">
                    Richiedi Informazioni
                  </Button>
                </Link>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll className="order-2 lg:order-1">
              <div className="relative overflow-hidden rounded-lg shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/man8hyey_dettagli%20vetrate.jpeg"
                  alt="Dettagli vetrate panoramiche VEPA - Qualità costruttiva"
                  className="w-full h-auto object-cover"
                />
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200} className="order-1 lg:order-2">
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  I Vantaggi
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 leading-tight mb-8">
                  Perché scegliere le nostre vetrate
                </h2>
                <div className="space-y-6">
                  {vetrateAdvantages.map((advantage, index) => (
                    <div key={index} className="border-l-2 border-sky-500 pl-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {advantage.title}
                      </h3>
                      <p className="text-gray-600 font-light">
                        {advantage.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Integration Section with New Image */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <FadeInOnScroll>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  Soluzioni Complete
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                  Integrazione perfetta con pergotende, bioclimatiche, e non solo
                </h2>
                <p className="text-lg text-gray-600 font-light leading-relaxed">
                  Le nostre vetrate si integrano perfettamente con pergotende e bioclimatiche, 
                  creando ambienti completamente chiusi e climatizzabili quando necessario.
                  Una soluzione ideale per vivere il tuo spazio esterno tutto l'anno.
                </p>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_64076d9f-5df4-4045-b4f3-caa897c80892/artifacts/upb2s2i1_vetrate.png"
                  alt="Vetrate panoramiche integrate con pergola bioclimatica"
                  className="w-full rounded-lg shadow-xl"
                />
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Vuoi chiudere il tuo spazio esterno?"
        description="Richiedi una consulenza gratuita per scoprire la soluzione vetrata ideale."
        variant="light"
      />
    </Layout>
  );
};

export default VetratePage;
